import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {AxiosError} from 'axios';
import {GlobalNotificationHandler} from 'utils/errorHandler';
import {ObsUser, ObsUserLabels} from 'types/entities/Obs';
import {queryClient} from 'App/App';

const userKey = 'obs/user';
const usersKey = 'obs/users';

export function useUsersView(
  errorHandler?: (error: AxiosError<any>) => void,
  options?: UseQueryOptions<ObsUser[], AxiosError, ObsUser[]>,
): UseQueryResult<ObsUser[], AxiosError<any>> {
  return useQuery(
    usersKey,
    (): ObsUser[] => {
      return (
        queryClient.getQueriesData<ObsUser>(userKey).map((x) => x[1]) ?? []
      );
    },
    {
      cacheTime: 0,
      staleTime: 0,
      ...options,
      onError: errorHandler || GlobalNotificationHandler,
    },
  );
}

export const useAddUser = () => {
  return useMutation(userKey, {
    onMutate: (data: ObsUser) => {
      const existing = queryClient.getQueryData<ObsUser>([
        userKey,
        {id: data.streamID},
      ]);
      queryClient.setQueryData<ObsUser>([userKey, {id: data.streamID}], {
        streamID: data.streamID,
        ...existing,
      });
      queryClient.invalidateQueries(usersKey);
    },
  });
};

export const useRemoveUser = () => {
  return useMutation(userKey, {
    onMutate: (streamID: string) => {
      queryClient.removeQueries([userKey, {id: streamID}]);
      queryClient.invalidateQueries(usersKey);
    },
  });
};

export const useSetUserNames = () => {
  return useMutation(userKey, {
    onMutate: (data: ObsUserLabels) => {
      Object.entries(data).forEach(([key, value]) =>
        queryClient.setQueryData<ObsUser>(
          [userKey, {id: key}],
          (oldData) =>
            <ObsUser>{
              ...oldData,
              label: key.includes('_') ? `${value} презентация` : value,
              streamID: key,
            },
        ),
      );
      queryClient.invalidateQueries(usersKey);
    },
  });
};
